// ─────────────────────────────────────────────────────────────────
// Glass-i — Voucher API client (Stage 6B.4).
//
// Thin axios wrappers around /api/erp/accounts/vouchers/*. Approval-MFA
// challenge id flows through the standard `x-approval-mfa-challenge-id`
// header on post / cancel.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/accounts';
const VOUCHERS = `${BASE}/vouchers`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// camelCase grid field → snake_case backend whitelist (mirrors the
// accountsApi pattern). Used by translateQuery on list().
const GRID_FIELD_TO_SQL = {
  voucherNo:     'voucher_no',
  voucherType:   'voucher_type',
  voucherDate:   'voucher_date',
  status:        'status',
  totalDebit:    'total_debit',
  totalCredit:   'total_credit',
  postingSource: 'posting_source',
  postedAt:      'posted_at',
  createdAt:     'created_at'
};
function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}
function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}
function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export const voucherApi = {
  list: (query) =>
    client.post(`${VOUCHERS}/list`, { ...(translateQuery(query) || {}), voucherType: 'journal' }).then(unwrap),
  get:  (id) => client.get(`${VOUCHERS}/${encodeURIComponent(id)}`).then(unwrap),
  createDraft: (payload) =>
    client.post(VOUCHERS, { ...payload, voucherType: 'journal' }).then(unwrap),
  updateDraft: (id, payload) =>
    client.put(`${VOUCHERS}/${encodeURIComponent(id)}`, payload).then(unwrap),
  post: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${VOUCHERS}/${encodeURIComponent(id)}/post`, {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  cancel: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${VOUCHERS}/${encodeURIComponent(id)}/cancel`, body || {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  // Stage 6B.9F — draft delete now carries the Approval-MFA challenge.
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${VOUCHERS}/${encodeURIComponent(id)}`, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),

  // ── Ledger lookup (consumed by the voucher form dropdown) ──
  ledgerLookup: (q, limit = 50) =>
    client.get(`${BASE}/ledgers/lookup`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap)
};

export default voucherApi;
